import React from "react";
import { ArrowRight, Phone, CheckCircle, Clock, MapPin, Calendar } from "@phosphor-icons/react";
import { HERO_BG } from "./assets";

const liveItems = [
  { city: "Debrecen", action: "ajánlatkérés", time: "2 perce" },
  { city: "Győr", action: "új projekt indul", time: "12 perce" },
  { city: "Pécs", action: "ajánlatkérés", time: "26 perce" },
  { city: "Szeged", action: "helyszínfelmérés", time: "1 órája" },
];

export default function Hero() {
  // Determine current week (1-based) for the "kapacitás" badge
  const week = Math.ceil((((new Date() - new Date(new Date().getFullYear(), 0, 1)) / 86400000) + 1) / 7);
  const nextAvailable = `${(week % 8) + 2}. hét`;

  return (
    <section id="top" data-testid="hero-section" className="relative pt-16 md:pt-20">
      <div className="relative min-h-[88vh] flex items-end">
        {/* Background image */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Modern szendvicspanel garázs" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#000000]/90 via-[#000000]/55 to-[#000000]/25" />
        </div>

        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-end">
            {/* Headline col */}
            <div className="lg:col-span-7">
              {/* Live ticker */}
              <div className="inline-flex items-center gap-3 bg-[#FFFFFF]/95 backdrop-blur px-4 py-2 rounded-sm mb-6 border border-[#FFFFFF]/40" data-testid="hero-live-ticker">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inset-0 bg-[#DC2626] rounded-full pulse-dot" />
                  <span className="relative inline-flex h-2 w-2 bg-[#DC2626] rounded-full" />
                </span>
                <span className="text-xs font-semibold text-[#0F0F0F]">
                  Élő ·
                </span>
                <div className="h-4 overflow-hidden text-xs font-medium text-[#0F0F0F]">
                  <div className="ticker-vertical-track">
                    {[...liveItems, liveItems[0]].map((it, i) => (
                      <div key={i} className="h-4 leading-4">
                        {it.city} — {it.action} · {it.time}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <h1 className="font-display font-black text-[#FFFFFF] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95] text-balance mb-6">
                Szendvicspanel garázs.<br/>
                <span className="text-[#FFFFFF]">Gyorsan. Egyedi méretben.</span>
              </h1>

              <p className="text-[#FFFFFF]/85 text-base md:text-lg max-w-xl mb-8 leading-relaxed">
                Felejtse el a hónapokig tartó építkezést. Mi méretre gyártjuk, és a szerződéskötéstől számított 1 hónapon belül felépítjük. Fix árral, 20 év szerkezeti garanciával.
              </p>

              <ul className="flex flex-col sm:flex-row gap-3 sm:gap-6 mb-10">
                <li className="flex items-center gap-2 text-[#FFFFFF]">
                  <CheckCircle size={20} weight="fill" className="text-[#FFFFFF]" />
                  <span className="text-sm font-medium">Egyedi méret, országosan</span>
                </li>
                <li className="flex items-center gap-2 text-[#FFFFFF]">
                  <Clock size={20} weight="fill" className="text-[#FFFFFF]" />
                  <span className="text-sm font-medium">Ajánlat 24 órán belül</span>
                </li>
                <li className="flex items-center gap-2 text-[#FFFFFF]">
                  <CheckCircle size={20} weight="fill" className="text-[#FFFFFF]" />
                  <span className="text-sm font-medium">20 év szerkezeti garancia</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="#quote" data-testid="hero-quote-btn" className="cta-light">
                  Árajánlatot kérek <ArrowRight size={18} weight="bold" />
                </a>
                <a href="#callback" data-testid="hero-callback-btn" className="cta-ghost">
                  <Phone size={18} weight="fill" /> Hívjanak vissza
                </a>
              </div>
            </div>

            {/* Smart info card */}
            <div className="lg:col-span-5">
              <div className="bg-[#FFFFFF] border border-[#E4E4E7] rounded-sm p-5 md:p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                  <span className="badge-chip" style={{ background: "#0F0F0F", color: "#FFFFFF" }}>
                    <Calendar size={12} weight="fill" /> Élő naptár
                  </span>
                  <span className="text-[10px] font-mono text-[#525252]">2025 / 12</span>
                </div>

                <div className="mb-5 pb-5 border-b border-[#E4E4E7]">
                  <div className="text-xs text-[#525252] uppercase tracking-wider mb-1">Legközelebbi szabad slot</div>
                  <div className="number-huge text-3xl md:text-4xl text-[#0F0F0F]">{nextAvailable}</div>
                  <div className="text-xs text-[#525252] mt-1">
                    <MapPin size={12} className="inline mr-1" /> Országosan elérhető
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-5">
                  {[
                    { v: "420+", l: "projekt" },
                    { v: "1 hó", l: "szerz. → kész" },
                    { v: "20 év", l: "garancia" },
                  ].map((s) => (
                    <div key={s.l} className="text-center p-2 bg-[#F5F5F5] rounded-sm">
                      <div className="font-display font-black text-base text-[#0F0F0F]">{s.v}</div>
                      <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-0.5">{s.l}</div>
                    </div>
                  ))}
                </div>

                {/* Today's bookings progress */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-[#525252]">Mai ajánlatkérések</span>
                    <span className="font-bold text-[#0F0F0F]">7 / 10</span>
                  </div>
                  <div className="h-1.5 bg-[#E4E4E7] rounded-sm overflow-hidden">
                    <div className="h-full bg-[#0F0F0F] rounded-sm" style={{ width: "70%", transition: "width 800ms ease" }} />
                  </div>
                  <div className="text-[10px] text-[#525252] mt-1.5">
                    <span className="pulse-dot inline-block w-1.5 h-1.5 bg-[#DC2626] mr-1 rounded-full" />
                    Még 3 helyet tudunk vállalni ma
                  </div>
                </div>

                <a href="#configurator" className="cta-primary w-full justify-center text-sm mt-3">
                  Tervező indítása <ArrowRight size={16} weight="bold" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="bg-[#0F0F0F] text-[#FFFFFF]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-8 md:py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "420+", l: "kivitelezett projekt" },
            { v: "1 hónap", l: "szerződéstől az átadásig" },
            { v: "24 óra", l: "ajánlat visszaküldés" },
            { v: "20 év", l: "szerkezeti garancia" },
          ].map((s, i) => (
            <div key={s.l} data-testid={`hero-stat-${i}`} className="text-center md:text-left">
              <div className="number-huge text-3xl md:text-4xl text-[#FFFFFF]">{s.v}</div>
              <div className="text-xs md:text-sm text-[#E4E4E7] mt-1 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
